<script setup lang="ts">
import UiButton from './UiButton/index'
import type { UiButtonProps } from './UiButton/index'
import UiIcon from './UiIcon.vue'
import { computed, inject, type VNode } from 'vue'
import { ToggleGroupKey } from './shared/toggleGroupContext'

export type UiToggleButtonProps = UiButtonProps & {
    toggleArrow?: boolean;
    label?: string;
    /** UiToggleGroup mode */
    value?: string | number
    disabled?: boolean
    noHover?: boolean
    disableActiveClasses?: boolean
    variant?: 'outline' | 'ghost' | 'primary'
    activeVariant?: 'outline' | 'ghost' | 'primary'
}

const model = defineModel<boolean | undefined>({ default: undefined });

const props = defineProps<UiToggleButtonProps>();

const group = inject(ToggleGroupKey, null);

const checked = computed(() => {
    if (group && props.value !== undefined) return group.selected() === props.value
    return !!model.value
});

function onClick() {
    if (props.disabled) return
    if (group && props.value !== undefined) {
        group.select(props.value)
    } else {
        model.value = !model.value
    }
}

function onMouseEnter() {
    if (group && props.value !== undefined && group.changeOnHover && !props.disabled) {
        group.select(props.value)
    }
}

const computedProps = computed(() => ({
    ...props,
    disabled: props.disabled || group?.disabled,
    variant: checked.value ? props.activeVariant || props.variant || 'ghost' : props.variant || 'ghost',
    noHover: checked.value ? true : props.noHover,
} as const));

const slots = defineSlots<{
    before_label: () => VNode;
    label: () => VNode;
    after_label: () => VNode;
    subtitle: () => VNode;
}>();
</script>

<template>
    <UiButton @click="onClick" @mouseenter="onMouseEnter" @focus="onMouseEnter" :class="{
        'bg-se-gray-200': checked && !(group && group.changeOnHover) && !props.disableActiveClasses,
        'hover:bg-se-gray-100': !checked && !noHover,
    }" v-bind="computedProps" :label="label"
        :aria-checked="['radio', 'checkbox', 'switch'].includes(String($attrs.role)) ? checked : undefined"
        :aria-pressed="props.href || $attrs['aria-expanded'] !== undefined || ['radio', 'checkbox', 'switch'].includes(String($attrs.role)) ? undefined : checked">
        <template #before_label>
            <slot name="before_label" />
        </template>
        <template #subtitle>
            <slot name="subtitle" />
        </template>
        <template #label>
            <slot name="label" />
        </template>
        <template #after_label>
            <slot name="after_label" />
            <UiIcon v-if="toggleArrow" name="chevron-down" :class="{
                'w-4 h-4': true,
                '-rotate-90': checked,
            }" />
        </template>
    </UiButton>
</template>